import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FlowingWave } from "../components/FlowingWave";
import mokachikaLogo from "../assets/mokachika.svg";
import langlapIcon from "../assets/langlap/app-512.png"; // LangLap's own app icon (langlap.com, tools/make_icons.py)

// Mokachika is where Raj Shekhar makes things people can use and play with (5 October 2026; a filter for deciding, not copy).
// Every tile is a work you can open now, in that work's own look and alive: no house style; the works are credited to the
// practice (the identity framework).

const seoMetadata = {
  title: "mokachika",
  description:
    "LangLap, a speed game about the world's writing; WORLDSTREAM, news and blogs from around the world, live; MokaStudio, ZeitTab, GeoTab, tatara and more, by Raj Shekhar.",
  keywords: "LangLap, WORLDSTREAM, MokaStudio, ZeitTab, GeoTab, tatara, claude-sandbox, ghostpi, SharkScope, Raj Shekhar",
  ogImage: mokachikaLogo,
  url: "https://mokachika.com",
};

const TOOLS = [
  { name: "claude-sandbox", line: "Claude Code and Codex with approvals off, in a container you throw away.", url: "https://github.com/rsh3khar/claude-sandbox" },
  { name: "ghostpi", line: "A Raspberry Pi 5 that is invisible on a shared network.", url: "https://github.com/rsh3khar/ghostpi" },
  { name: "SharkScope", line: "Your network traffic, live, on a map or a globe.", url: "https://github.com/rsh3khar/sharkscope" },
  { name: "cclearn", line: "Teaches Claude Code how you work, from your own conversations.", url: "https://github.com/rsh3khar/cc-plugins" },
  { name: "apple-notes", line: "Apple Notes out as clean Markdown.", url: "https://github.com/rsh3khar/apple-notes" },
  { name: "EZ Epoch", line: "Unix timestamps, readable.", url: "https://chromewebstore.google.com/detail/ez-epoch/negaffjlgjiboiamfcholgaohdmoaami" },
  { name: "TextDesigner", line: "A canvas for designing with text.", url: "/text" },
  { name: "LayoutJSON", line: "A visual builder for layouts as JSON.", url: "/layout" },
];

const LAP_ONE = Date.UTC(2026, 9, 4); // Lap 1 of LangLap's daily: 4 October 2026, 00:00 UTC
const todaysLap = () => Math.max(1, Math.floor((Date.now() - LAP_ONE) / 86_400_000) + 1);
const still = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const useEvery = (ms: number, alsoWhenStill = false) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (still() && !alsoWhenStill) return;
    const id = window.setInterval(() => setN((v) => v + 1), ms);
    return () => window.clearInterval(id);
  }, [ms, alsoWhenStill]);
  return n;
};

const LangLapTile = () => {
  useEvery(60_000, true); // the lap number turns over at 00:00 UTC even on a page left open
  return (
    <a className="t ll" href="https://langlap.com" target="_blank" rel="noopener">
      <span className="lap">Lap {todaysLap()} is open</span>
      <img className="icon" src={langlapIcon} alt="" />
      <span className="name">LangLap</span>
    </a>
  );
};

const StreamTile = () => {
  const lines = [];
  for (let lane = 0; lane < 8; lane++)
    for (let half = 0; half < 2; half++) {
      const dur = 9 + ((lane * 5) % 6);
      lines.push(
        <i key={`${lane}-${half}`} className={(lane + half) % 4 === 0 ? "b" : ""}
          style={{ top: 24 + lane * 18, width: 70 + (((lane * 2 + half) * 53) % 150), animationDuration: `${dur}s`, animationDelay: `-${(lane * 1.7 + (half * dur) / 2).toFixed(2)}s` }} />
      );
    }
  return (
    <a className="t ws" href="https://worldstream.io" target="_blank" rel="noopener">
      <span className="yr">● live</span>
      <div aria-hidden="true">{lines}</div>
      <span className="name">WORLDSTREAM</span>
    </a>
  );
};

const StudioTile = () => {
  const step = useEvery(180) % 8;
  const on = new Set([0, 4, 10, 14, 18, 22, 25, 29]);
  return (
    <a className="t ms" href="/music" target="_blank" rel="noopener">
      <div className="pads" aria-hidden="true">
        {Array.from({ length: 32 }, (_, k) => <b key={k} className={`${on.has(k) ? "on" : ""} ${k % 8 === step ? "now" : ""}`} />)}
      </div>
      <span className="name">MokaStudio</span>
    </a>
  );
};

const ClockTile = () => {
  useEvery(1000, true);
  const d = new Date(), s = d.getSeconds(), m = d.getMinutes() + s / 60, h = (d.getHours() % 12) + m / 60;
  return (
    <a className="t zt" href="https://zeit.mokachika.com" target="_blank" rel="noopener">
      <svg width="150" height="150" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="46" fill="none" stroke="#F2EEE6" strokeOpacity=".5" />
        <line x1="50" y1="50" x2="50" y2="26" stroke="#F2EEE6" strokeWidth="3" strokeLinecap="round" transform={`rotate(${h * 30} 50 50)`} />
        <line x1="50" y1="50" x2="50" y2="14" stroke="#F2EEE6" strokeWidth="2" strokeLinecap="round" transform={`rotate(${m * 6} 50 50)`} />
        <line x1="50" y1="54" x2="50" y2="10" stroke="#E8B47A" strokeWidth="1" transform={`rotate(${s * 6} 50 50)`} />
        <circle cx="50" cy="50" r="2.5" fill="#E8B47A" />
      </svg>
      <span className="name">ZeitTab</span>
    </a>
  );
};

const GlobeTile = () => {
  const ph = useEvery(50) * 0.03;
  return (
    <a className="t gt" href="https://geotab-web.mokachika.com" target="_blank" rel="noopener">
      <svg width="170" height="170" viewBox="-60 -60 120 120" aria-hidden="true">
        <circle r="54" fill="#E8F2FA" stroke="#16324F" strokeWidth="1.5" />
        <g stroke="#16324F" strokeOpacity=".45" fill="none">
          {[0, 1, 2, 3, 4, 5].map((k) => <ellipse key={k} rx={Math.abs(54 * Math.cos(ph + (k * Math.PI) / 6))} ry="54" />)}
          <ellipse rx="54" ry="18" />
          <line x1="-54" x2="54" />
        </g>
      </svg>
      <span className="name">GeoTab</span>
    </a>
  );
};

const STYLE = `
.mk { background:#FBFAF7; color:#1A1A1A; font-family:Inter, Geist, system-ui, sans-serif; min-height:100vh; }
.mk header { text-align:center; padding:48px 20px 0; }
.mk .wave > div { margin-bottom:4px !important; }
.mk h1 { font-family:"Instrument Serif", serif; font-weight:400; font-size:64px; margin:0; line-height:1; }
.mk header a { display:inline-block; margin-top:10px; color:#77736B; font-size:15px; text-decoration:none; } .mk header a:hover { color:#1A1A1A; }
.mk .grid { max-width:1120px; margin:0 auto; padding:0 24px; display:grid; grid-template-columns:repeat(3, 1fr); gap:18px; }
.mk .t { position:relative; height:230px; border-radius:14px; overflow:hidden; text-decoration:none; color:inherit; border:2px solid #1A1A1A; transition:transform .15s; }
.mk .t:hover { transform:rotate(-.6deg) scale(1.01); }
.mk .t .name { position:absolute; left:18px; bottom:14px; font-size:30px; line-height:1; } .mk .t .yr { position:absolute; right:16px; top:12px; font-size:13px; opacity:.75; }
.mk .ll { background:#E9FF5A; color:#111214; } .mk .ll .name { font-family:"Mona Sans"; font-weight:800; font-stretch:125%; }
.mk .ll .icon { position:absolute; right:26px; top:26px; width:128px; height:128px; border-radius:30px; border:2px solid #111214; transform:rotate(4deg); }
.mk .ll .lap { position:absolute; left:18px; top:14px; font-weight:600; font-size:14px; }
.mk .ws { background:#06070A; } .mk .ws .name { font-family:"SF Mono", Menlo, monospace; color:#4ADE80; letter-spacing:.16em; font-size:24px; font-weight:700; }
.mk .ws .yr { color:#4ADE80; } .mk .ws i { position:absolute; left:0; height:6px; border-radius:3px; background:#1F3B2A; animation:mk-run linear infinite; } .mk .ws i.b { background:#4ADE80; opacity:.7; height:4px; }
@keyframes mk-run { from { transform:translateX(380px); } to { transform:translateX(-420px); } }
.mk .ms { background:#EFE9DD; } .mk .ms .name { font-family:"Bricolage Grotesque"; font-weight:800; }
.mk .ms .pads { position:absolute; right:22px; top:22px; display:grid; grid-template-columns:repeat(8, 22px); gap:6px; padding:10px; background:#2A2A2A; border-radius:10px; }
.mk .ms .pads b { width:22px; height:22px; border-radius:4px; background:#D8D3C8; } .mk .ms .pads b.on { background:#FF6B35; } .mk .ms .pads b.now { outline:2px solid #fff; }
.mk .zt { background:linear-gradient(160deg, #1D2333, #3B3F57); color:#F2EEE6; } .mk .zt .name { font-family:"Cormorant Garamond"; font-size:36px; } .mk .zt svg { position:absolute; right:26px; top:22px; }
.mk .gt { background:#CFE3F2; } .mk .gt .name { font-family:"Instrument Serif"; font-size:36px; color:#16324F; } .mk .gt svg { position:absolute; right:22px; top:16px; }
.mk .ta { background:#141210; color:#F4E9DA; } .mk .ta .name { font-weight:600; letter-spacing:-.02em; }
.mk .ta .ingot { position:absolute; right:34px; top:48px; width:150px; height:64px; border-radius:8px 8px 14px 14px;
  background:radial-gradient(ellipse at 50% 40%, #FFE9A8 0%, #FFB347 28%, #E0561F 60%, #5C1A08 100%); box-shadow:0 0 60px 6px rgba(255,120,40,.45); animation:mk-glow 3.4s ease-in-out infinite; }
@keyframes mk-glow { 50% { box-shadow:0 0 80px 14px rgba(255,140,50,.6); filter:brightness(1.12); } }
.mk .ta .kanji { position:absolute; right:22px; bottom:12px; font-family:"Noto Serif JP"; font-size:22px; opacity:.35; }
.mk h2 { max-width:1120px; margin:42px auto 12px; padding:0 24px; font-size:15px; font-weight:600; }
.mk .tools { max-width:1120px; margin:0 auto; padding:0 24px; display:grid; grid-template-columns:repeat(4, 1fr); gap:12px; }
.mk .tool { border:1.5px solid #1A1A1A; border-radius:10px; padding:14px 14px 12px; text-decoration:none; color:inherit; background:#fff; }
.mk .tool b { display:block; font-size:16px; } .mk .tool span { color:#77736B; font-size:13.5px; line-height:1.4; display:block; margin-top:4px; }
.mk .tool:hover { background:#1A1A1A; color:#fff; } .mk .tool:hover span { color:#bbb; }
.mk footer { text-align:center; padding:48px 0 40px; color:#9A968E; font-size:12px; } .mk footer nav { display:flex; justify-content:center; gap:4px; margin-bottom:10px; }
.mk footer .say { color:#55524C; font-size:15px; margin:0 0 18px; } .mk footer .say a { color:#1A1A1A; text-decoration:underline; text-underline-offset:3px; }
.mk footer nav { align-items:center; } .mk footer nav a.mail { font-size:14px; color:#55524C; text-decoration:underline; text-underline-offset:3px; }
.mk footer nav a { padding:10px; color:#77736B; } .mk footer nav a:hover { color:#1A1A1A; }
@media (max-width:860px) { .mk .grid { grid-template-columns:1fr; } .mk .tools { grid-template-columns:1fr 1fr; } .mk h1 { font-size:48px; } }
@media (prefers-reduced-motion: reduce) { .mk * { animation:none !important; } }
`;

export const HomePage = () => (
  <>
    <Helmet>
      <title>{seoMetadata.title}</title>
      <meta name="description" content={seoMetadata.description} />
      <meta name="keywords" content={seoMetadata.keywords} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoMetadata.url} />
      <meta property="og:title" content={seoMetadata.title} />
      <meta property="og:description" content={seoMetadata.description} />
      <meta property="og:image" content={seoMetadata.ogImage} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={seoMetadata.url} />
      <meta property="twitter:title" content={seoMetadata.title} />
      <meta property="twitter:description" content={seoMetadata.description} />
      <meta property="twitter:image" content={seoMetadata.ogImage} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={seoMetadata.url} />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Mona+Sans:wdth,wght@125,800&family=Cormorant+Garamond:wght@500&family=Noto+Serif+JP:wght@700&family=Bricolage+Grotesque:opsz,wght@96,800&display=swap"
      />
    </Helmet>
    <style>{STYLE}</style>
    <div className="mk">
      <header>
        <h1>mokachika</h1>
        <a href="https://rshekhar.com" target="_blank" rel="noopener noreferrer">by Raj Shekhar</a>
      </header>
      <div className="wave"><FlowingWave tone="ink" /></div>
      <div className="grid">
        <LangLapTile />
        <StreamTile />
        <StudioTile />
        <ClockTile />
        <GlobeTile />
        <a className="t ta" href="https://github.com/rsh3khar/tatara" target="_blank" rel="noopener">
          <div className="ingot" aria-hidden="true" />
          <span className="kanji" aria-hidden="true">たたら</span>
          <span className="name">tatara</span>
        </a>
      </div>
      <h2>Tools</h2>
      <div className="tools">
        {TOOLS.map((t) => (
          <a key={t.name} className="tool" href={t.url} target="_blank" rel="noopener noreferrer">
            <b>{t.name}</b>
            <span>{t.line}</span>
          </a>
        ))}
      </div>
      <footer>
        <nav>
          <a href="https://x.com/rsh3khar" target="_blank" rel="noopener noreferrer" aria-label="X">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
          </a>
          <a href="https://github.com/rsh3khar" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg>
          </a>
        </nav>
        <p className="say">
          Write to <a href="mailto:contact@mokachika.com">contact@mokachika.com</a>
        </p>
        <p className="say">
          If something here helped you,{" "}
          <a href="https://www.buymeacoffee.com/mokachika" target="_blank" rel="noopener noreferrer">buy me a coffee</a>.
        </p>
        © {new Date().getFullYear()} mokachika
      </footer>
    </div>
  </>
);
